import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, Link2, Trash2, ExternalLink } from "lucide-react";
import { CATEGORIES } from "@/data/portfolioData";

const AlbumManager = ({ open, onClose, customAlbums, onAdd, onRemove }) => {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(CATEGORIES[1]);
  const [url, setUrl] = useState("");
  const [cover, setCover] = useState("");
  const [location, setLocation] = useState("");

  const isGoogleLink = (link) =>
    /photos\.app\.goo\.gl|photos\.google\.com/.test(link);

  const submit = (e) => {
    e.preventDefault();
    if (!title.trim() || !url.trim()) {
      toast.error("Add an album title and a Google Photos share link.");
      return;
    }
    if (!isGoogleLink(url)) {
      toast.error("That doesn't look like a Google Photos share link.");
      return;
    }
    onAdd({
      id: `custom-${Date.now()}`,
      title: title.trim(),
      category,
      location: location.trim() || "Google Photos Album",
      price: "",
      sqft: "",
      photos: cover.trim()
        ? [{ src: cover.trim(), caption: title.trim(), exif: "Google Photos import" }]
        : [],
      googleAlbumUrl: url.trim(),
      isCustom: true,
    });
    toast.success(`"${title.trim()}" linked. It now appears in your gallery.`);
    setTitle("");
    setUrl("");
    setCover("");
    setLocation("");
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="album-manager-modal-container"
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#0A0A0C]/90 p-4 backdrop-blur-md"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.3 }}
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-white/10 bg-[#141419] p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="eyebrow-label mb-2">Album Sync</p>
                <h3 className="font-display text-xl font-bold text-zinc-50">Link a Google Photos album</h3>
                <p className="mt-2 text-xs leading-relaxed text-zinc-400">
                  In Google Photos: open the album → Share → Create link → paste it here.
                  It will appear as a new property album on your site.
                </p>
              </div>
              <button
                onClick={onClose}
                data-testid="album-manager-close-btn"
                className="rounded-full border border-white/15 p-2 text-zinc-400 hover:border-[#D4AF37]/60 hover:text-[#E2C275]"
                aria-label="Close album manager"
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={submit} className="mt-7 flex flex-col gap-4">
              <div>
                <label className="mb-2 block text-xs font-medium text-zinc-400">Property / album title *</label>
                <input
                  data-testid="album-import-title-input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. 1200 Bel Air Rd"
                  className="input-lux"
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-medium text-zinc-400">Google Photos share link *</label>
                <input
                  data-testid="album-import-url-input"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://photos.app.goo.gl/…"
                  className="input-lux"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-2 block text-xs font-medium text-zinc-400">Category</label>
                  <select
                    data-testid="album-import-type-select"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="input-lux"
                  >
                    {CATEGORIES.filter((c) => c !== "All").map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-xs font-medium text-zinc-400">Location</label>
                  <input
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="Bel-Air, CA"
                    className="input-lux"
                  />
                </div>
              </div>
              <div>
                <label className="mb-2 block text-xs font-medium text-zinc-400">Cover image URL (optional)</label>
                <input
                  value={cover}
                  onChange={(e) => setCover(e.target.value)}
                  placeholder="https://… (shown as the album cover)"
                  className="input-lux"
                />
              </div>
              <button type="submit" data-testid="album-import-submit-btn" className="btn-gold mt-2 w-full">
                <Link2 size={16} /> Link Album
              </button>
            </form>

            {customAlbums.length > 0 && (
              <div className="mt-8 border-t border-white/[0.08] pt-6">
                <p className="font-tech mb-4 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  Linked albums ({customAlbums.length})
                </p>
                <div className="flex flex-col gap-3">
                  {customAlbums.map((a) => (
                    <div
                      key={a.id}
                      className="flex items-center justify-between rounded-xl border border-white/[0.08] bg-[#121216] px-4 py-3"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-zinc-200">{a.title}</div>
                        <a
                          href={a.googleAlbumUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-0.5 flex items-center gap-1 text-[11px] text-emerald-400 hover:underline"
                        >
                          <ExternalLink size={10} /> Open in Google Photos
                        </a>
                      </div>
                      <button
                        onClick={() => { onRemove(a.id); toast.success(`Removed "${a.title}".`); }}
                        data-testid="album-remove-btn"
                        className="ml-3 rounded-full border border-white/10 p-2 text-zinc-500 transition-colors hover:border-red-400/50 hover:text-red-400"
                        aria-label={`Remove ${a.title}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AlbumManager;
